import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRealtimeStore } from "../../lib/store.js";
import {
  internalApi,
  type InternalTemplateSectionRecord,
  type InternalTrustAssetRecord,
  type InternalTrustAssetUsageRecord
} from "../../lib/internal-api.js";
import { focusChatComposer } from "../../lib/composer-focus.js";
import type { MessageContent, TrustAssetKind } from "@tg/shared";

export type TrustFilterKind = "all" | TrustAssetKind;

export type TrustNav =
  | { mode: "browse"; sectionId: string | null }
  | { mode: "unsorted" }
  | { mode: "all" };

/**
 * Ядро панели шаблонов/траста, вынесенное из chat-view для переиспользования
 * в правой панели редизайна. Данные и действия те же (те же API и стор-экшены
 * sendMessage/sendAlbum), поэтому поведение идентично прежней панели.
 *
 * `active` — открыта ли вкладка (Шаблоны/Оплата): по нему грузим ассеты/секции
 * и историю использования в текущем чате.
 */
export const useTrustTemplates = (active: boolean) => {
  const accountId = useRealtimeStore((s) => s.accountId);
  const wsUrl = useRealtimeStore((s) => s.wsUrl);
  const selectedChatId = useRealtimeStore((s) => s.selectedChatId);
  const sendMessage = useRealtimeStore((s) => s.sendMessage);
  const sendAlbum = useRealtimeStore((s) => s.sendAlbum);

  const [assets, setAssets] = useState<InternalTrustAssetRecord[]>([]);
  const [sections, setSections] = useState<InternalTemplateSectionRecord[]>([]);
  const [usage, setUsage] = useState<Record<string, InternalTrustAssetUsageRecord>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterKind, setFilterKind] = useState<TrustFilterKind>("all");
  const [filterQuery, setFilterQuery] = useState("");
  const [nav, setNav] = useState<TrustNav>({ mode: "browse", sectionId: null });
  const [usageBusyId, setUsageBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!accountId) {
      setAssets([]);
      setSections([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [assetsResponse, sectionsResponse] = await Promise.all([
        internalApi.listTrustAssets(accountId, wsUrl),
        internalApi.listTemplateSections(accountId, wsUrl)
      ]);
      setAssets(assetsResponse.assets);
      setSections(sectionsResponse.sections);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось загрузить шаблоны.");
    } finally {
      setIsLoading(false);
    }
  }, [accountId, wsUrl]);

  // Грузим ассеты/секции при открытии вкладки.
  useEffect(() => {
    if (!active) return;
    void reload();
  }, [active, reload]);

  // История использования per-chat.
  useEffect(() => {
    if (!active || !accountId || !selectedChatId) return;
    let cancelled = false;
    void internalApi
      .listTrustAssetUsage(accountId, selectedChatId, wsUrl)
      .then(({ usage: rows }) => {
        if (cancelled) return;
        setUsage(Object.fromEntries(rows.map((entry) => [entry.assetId, entry])));
      })
      .catch(() => {
        /* плашка косметическая — молча без неё */
      });
    return () => {
      cancelled = true;
    };
  }, [active, accountId, selectedChatId, wsUrl]);

  const sectionIds = useMemo(
    () => new Set(sections.map((section) => section.sectionId)),
    [sections]
  );
  const navResolved = useMemo<TrustNav>(
    () =>
      nav.mode === "browse" && nav.sectionId !== null && !sectionIds.has(nav.sectionId)
        ? { mode: "browse", sectionId: null }
        : nav,
    [nav, sectionIds]
  );
  const hasSections = sections.length > 0;
  const rootSections = useMemo(
    () => sections.filter((section) => section.parentId === null),
    [sections]
  );
  const sectionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    let orphans = 0;
    for (const asset of assets) {
      if (asset.sectionId && sectionIds.has(asset.sectionId)) {
        counts.set(asset.sectionId, (counts.get(asset.sectionId) ?? 0) + 1);
      } else {
        orphans += 1;
      }
    }
    return { counts, orphans };
  }, [assets, sectionIds]);

  const assetsVisible =
    !hasSections ||
    navResolved.mode === "all" ||
    navResolved.mode === "unsorted" ||
    (navResolved.mode === "browse" && navResolved.sectionId !== null);

  const normalizedQuery = filterQuery.trim().toLowerCase();
  const filteredAssets = useMemo(
    () =>
      assets.filter((asset) => {
        if (hasSections) {
          if (navResolved.mode === "unsorted") {
            if (asset.sectionId && sectionIds.has(asset.sectionId)) return false;
          } else if (navResolved.mode === "browse") {
            if (navResolved.sectionId === null) return false;
            if (asset.sectionId !== navResolved.sectionId) return false;
          }
        }
        if (filterKind !== "all" && asset.mediaKind !== filterKind) return false;
        if (normalizedQuery.length === 0) return true;
        const haystack = `${asset.title} ${asset.description ?? ""} ${
          asset.supportDescription ?? ""
        }`.toLowerCase();
        return haystack.includes(normalizedQuery);
      }),
    [assets, filterKind, hasSections, navResolved, normalizedQuery, sectionIds]
  );

  const markUsedLocally = useCallback(
    (assetId: string) => {
      if (!selectedChatId) return;
      const now = new Date().toISOString();
      setUsage((current) => {
        const previous = current[assetId];
        return {
          ...current,
          [assetId]: {
            assetId,
            chatId: selectedChatId,
            usedCount: (previous?.usedCount ?? 0) + 1,
            source: previous?.source ?? "auto",
            firstUsedAt: previous?.firstUsedAt ?? now,
            lastUsedAt: now
          }
        };
      });
    },
    [selectedChatId]
  );

  const toggleUsed = useCallback(
    async (assetId: string, used: boolean) => {
      if (!accountId || !selectedChatId) return;
      setUsageBusyId(assetId);
      try {
        const { usage: rows } = await internalApi.setTrustAssetUsage(
          { accountId, chatId: selectedChatId, assetId, used },
          wsUrl
        );
        setUsage(Object.fromEntries(rows.map((entry) => [entry.assetId, entry])));
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Не удалось изменить пометку.");
      } finally {
        setUsageBusyId(null);
      }
    },
    [accountId, selectedChatId, wsUrl]
  );

  const sendingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);
  const send = useCallback(
    (asset: InternalTrustAssetRecord, contentOverride?: MessageContent) => {
      if (sendingRef.current || !selectedChatId) return;
      sendingRef.current = true;
      setIsSending(true);
      window.setTimeout(() => {
        sendingRef.current = false;
        setIsSending(false);
      }, 700);
      const extra = asset.extraContents ?? [];
      if (extra.length > 0 && asset.groupMedia !== false) {
        sendAlbum(selectedChatId, [contentOverride ?? asset.content, ...extra], asset.assetId);
      } else if (extra.length > 0) {
        for (const content of [contentOverride ?? asset.content, ...extra]) {
          sendMessage(selectedChatId, content, undefined, undefined, asset.assetId);
        }
      } else {
        sendMessage(selectedChatId, contentOverride ?? asset.content, undefined, undefined, asset.assetId);
      }
      markUsedLocally(asset.assetId);
      focusChatComposer();
    },
    [markUsedLocally, selectedChatId, sendAlbum, sendMessage]
  );

  return {
    accountId,
    wsUrl,
    selectedChatId,
    assets,
    sections,
    rootSections,
    sectionCounts,
    usage,
    isLoading,
    error,
    filterKind,
    setFilterKind,
    filterQuery,
    setFilterQuery,
    nav: navResolved,
    setNav,
    hasSections,
    assetsVisible,
    filteredAssets,
    usageBusyId,
    isSending,
    reload,
    toggleUsed,
    send
  };
};
